import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Edit } from "lucide-react";
import AddSplitsModal from "@/components/portal/AddSplitsModal";
import EditSplitsModal from "@/components/portal/EditSplitsModal";

export default function SplitsManager({ policy, onSave }) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  
  const splits = policy._split_of_policy || [];
  
  const getMainAgentPercent = () => {
    if (splits.length === 0) return 100;
    const totalSplits = splits.reduce((sum, s) => sum + parseFloat(s.agent_on_split_percent || 0), 0);
    return 100 - totalSplits;
  };

  const handleSuccess = () => {
    // Refresh policy list
    onSave();
  };

  if (splits.length === 0) {
    return (
      <>
        <div className="mt-3 bg-white rounded-xl p-6 border border-neutral-200 text-center">
          <p className="text-sm text-neutral-500 mb-3">No splits configured for this policy</p>
          {!policy.isLocked && (
            <Button
              onClick={() => setShowAddModal(true)}
              size="sm"
              className="bg-[#d4af37] hover:bg-[#b8941e] text-white rounded-xl"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Splits
            </Button>
          )}
        </div>
        
        <AddSplitsModal
          open={showAddModal}
          onOpenChange={setShowAddModal}
          policy={policy}
          onSuccess={handleSuccess}
        />
      </>
    );
  }

  return (
    <>
      <div className="mt-3 bg-white rounded-xl p-4 border border-neutral-200">
        {/* Main Agent Info */}
        <div className="mb-4 pb-4 border-b border-neutral-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-700">Main Agent (Owner)</p>
              <p className="text-xs text-neutral-500">{policy.agentOwner_name || policy.policyAgent_owner || 'Primary'}</p>
            </div>
            <Badge className="bg-[#d4af37] text-white rounded-full">
              {getMainAgentPercent()}%
            </Badge>
          </div>
        </div>

        {/* Splits List */}
        <div className="space-y-3 mb-4">
          {splits.map((split, index) => (
            <div key={split.id} className="flex items-center justify-between p-3 bg-neutral-50 rounded-xl">
              <div className="flex-1">
                <p className="text-sm font-medium text-neutral-800">{split.onsplit_name || 'N/A'}</p>
                <p className="text-xs text-neutral-500">NPN: {split.onsplit_npn || 'N/A'}</p>
              </div>
              <Badge variant="outline" className="border-[#d4af37] text-[#b8941e] rounded-full">
                {split.agent_on_split_percent}%
              </Badge>
            </div>
          ))}
        </div>

        {/* Total */}
        <div className="pt-4 border-t border-neutral-200 flex items-center justify-between">
          <p className="text-sm font-medium text-neutral-700">Total Allocated</p>
          <Badge className="bg-green-500 text-white rounded-full">100%</Badge>
        </div>

        {/* Action Buttons */}
        {!policy.isLocked && (
          <div className="mt-4 flex justify-end">
            <Button 
              onClick={() => setShowEditModal(true)} 
              size="sm" 
              variant="outline"
              className="border-[#d4af37] text-[#b8941e] hover:bg-[#f4e5b8] rounded-xl"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit Splits
            </Button>
          </div>
        )}
      </div>

      <EditSplitsModal
        open={showEditModal}
        onOpenChange={setShowEditModal}
        policy={policy}
        onSuccess={handleSuccess}
      />
    </>
  );
}
